import { useCallback, useEffect, useReducer, useRef } from "react";

import {
  type PublicChatMessage,
  startSession,
  submitEmail as apiSubmitEmail,
  submitMessage,
} from "../api/publicChat";
import {
  clearToken,
  getChatId,
  getSavedMessages,
  getToken,
  saveMessages,
  setChatId,
  setToken,
} from "../utils/sessionToken";
import { reportPublicJanError } from "../utils/reportPublicJanError";
import { pushJanEmailSubmit } from "../utils/analytics";

export type PublicChatStatus =
  | "idle"
  | "starting-session"
  | "loading-history"
  | "ready"
  | "streaming"
  | "awaiting-email"
  | "error";

export interface PublicChatState {
  status: PublicChatStatus;
  chatId: string | null;
  messages: PublicChatMessage[];
  pendingMessage: string | null;
  streamingContent: string;
  error: string | null;
}

type Action =
  | { type: "session/start" }
  | { type: "session/started"; chatId: string }
  | { type: "history/loaded"; messages: PublicChatMessage[] }
  | { type: "submit/start"; userMessage: PublicChatMessage; pending: string }
  | { type: "submit/resume" }
  | { type: "submit/chunk"; chunk: string }
  | { type: "submit/done"; assistant: PublicChatMessage }
  | { type: "submit/require-email" }
  | { type: "email/submitted" }
  | { type: "chatId/replaced"; chatId: string }
  | { type: "error"; message: string }
  | { type: "reset" };

const initial: PublicChatState = {
  status: "idle",
  chatId: null,
  messages: [],
  pendingMessage: null,
  streamingContent: "",
  error: null,
};

const reducer = (state: PublicChatState, action: Action): PublicChatState => {
  switch (action.type) {
    case "session/start":
      return { ...state, status: "starting-session", error: null };
    case "session/started":
      return { ...state, status: "loading-history", chatId: action.chatId };
    case "history/loaded":
      return { ...state, status: "ready", messages: action.messages };
    case "submit/start":
      return {
        ...state,
        status: "streaming",
        messages: [...state.messages, action.userMessage],
        pendingMessage: action.pending,
        streamingContent: "",
        error: null,
      };
    case "submit/resume":
      // Resume streaming for an already-submitted user turn (e.g. after the
      // email gate). Leaves messages and pendingMessage intact — the user
      // bubble was appended on the original submit/start.
      return {
        ...state,
        status: "streaming",
        streamingContent: "",
        error: null,
      };
    case "submit/chunk":
      return {
        ...state,
        streamingContent: state.streamingContent + action.chunk,
      };
    case "submit/done":
      return {
        ...state,
        status: "ready",
        messages: [...state.messages, action.assistant],
        pendingMessage: null,
        streamingContent: "",
      };
    case "submit/require-email":
      return { ...state, status: "awaiting-email" };
    case "email/submitted":
      return { ...state, status: "ready" };
    case "chatId/replaced":
      return { ...state, chatId: action.chatId };
    case "error":
      return { ...state, status: "error", error: action.message };
    case "reset":
      return initial;
  }
};

export interface BootstrapArgs {
  // Cloudflare Turnstile token. Required for /session/start when the
  // backend has TURNSTILE_SECRET_KEY set; ignored otherwise.
  turnstileToken?: string;
}

export interface UsePublicChatMachine extends PublicChatState {
  /** Returns the active chat id on success, or null if bootstrap failed
   *  (errors are surfaced via state, not thrown). */
  bootstrap: (args?: BootstrapArgs) => Promise<string | null>;
  submit: (message: string) => Promise<void>;
  submitEmail: (email: string, professionId: string) => Promise<void>;
  retryPending: () => Promise<void>;
  reset: () => void;
}

export interface UsePublicChatMachineOptions {
  /**
   * Called when the hook needs to obtain a fresh Cloudflare Turnstile token
   * mid-turn (used by stale-session recovery to re-bootstrap). Returns the
   * token, or `undefined` when Turnstile is not configured (local dev). The
   * implementation is expected to reset the widget and await onSuccess.
   * If the promise rejects or doesn't settle, recovery surfaces an error.
   */
  getTurnstileToken?: () => Promise<string | undefined>;
  /**
   * Variant slug this chat session is scoped to. Defaults to "default", which
   * uses the legacy un-namespaced storage keys so the /jan-demo page behaves
   * exactly as before. Any other variant scopes the session token, chat id and
   * message history to that variant's namespaced keys.
   */
  variant?: string;
}

export const usePublicChatMachine = (
  options: UsePublicChatMachineOptions = {}
): UsePublicChatMachine => {
  const variant = options.variant ?? "default";
  const [state, dispatch] = useReducer(reducer, initial);
  const getTurnstileTokenRef = useRef(options.getTurnstileToken);
  useEffect(() => {
    getTurnstileTokenRef.current = options.getTurnstileToken;
  }, [options.getTurnstileToken]);
  const bootstrapPromiseRef = useRef<Promise<string | null> | null>(null);
  // Gate persistence on a successful bootstrap so the initial empty state
  // doesn't clobber sessionStorage before we've read from it. Also keeps the
  // effect deps minimal so status-only transitions don't trigger a re-save.
  const hasBootstrappedRef = useRef(false);
  // Mirror of the active chat id, set synchronously the moment a session is
  // minted/reused. submit()/retryPending() read this instead of state.chatId so
  // a lazy "bootstrap then submit in the same call" flow (the embed) doesn't see
  // a stale state.chatId closure and wrongly dispatch no_chat_id. The /jan-demo
  // page bootstraps eagerly, so for it the ref simply mirrors state.
  const chatIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hasBootstrappedRef.current) return;
    saveMessages(state.messages, variant);
  }, [state.messages, variant]);

  /**
   * POSTs /session/start and writes the resulting token+chat_id to
   * localStorage. Does NOT dispatch reducer actions — callers decide whether
   * this is a visible bootstrap (with status transitions) or a silent
   * mid-turn recovery (state.chatId updated via chatId/replaced only).
   */
  const mintFreshSessionSilent = useCallback(
    async (args: { turnstileToken?: string }): Promise<string> => {
      const res = await startSession({
        turnstileToken: args.turnstileToken,
        variant,
      });
      setToken(res.token, variant);
      setChatId(res.chat_id, variant);
      chatIdRef.current = res.chat_id;
      return res.chat_id;
    },
    [variant]
  );

  /**
   * Sends the message to the backend and dispatches submit/chunk for each
   * SSE chunk. Terminates with submit/done or submit/require-email. Does
   * NOT dispatch submit/start — the caller is responsible for that exactly
   * once per user turn, so a recovery retry doesn't add a second user bubble.
   */
  const streamAssistantResponse = useCallback(
    async (chatId: string, message: string): Promise<void> => {
      let acc = "";
      const result = await submitMessage(
        chatId,
        message,
        (chunk) => {
          acc += chunk;
          dispatch({ type: "submit/chunk", chunk });
        },
        variant
      );

      if (result.kind === "email_required") {
        dispatch({ type: "submit/require-email" });
        return;
      }

      const assistant: PublicChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: acc,
        created_at: new Date().toISOString(),
      };
      dispatch({ type: "submit/done", assistant });
    },
    [variant]
  );

  /**
   * Streams the assistant response with one-shot 401 recovery (mint fresh
   * session, swap chatId, retry once). Does NOT touch messages or
   * pendingMessage — the caller owns the "did this turn already place a user
   * bubble?" decision via submit/start vs. submit/resume.
   */
  const streamWithRecovery = useCallback(
    async (chatId: string, message: string): Promise<void> => {
      let recoveryAttempted = false;

      const attempt = async (currentChatId: string): Promise<void> => {
        try {
          await streamAssistantResponse(currentChatId, message);
        } catch (e) {
          const is401 = e instanceof Error && e.message === "http_401";
          if (!is401 || recoveryAttempted) {
            throw e;
          }
          recoveryAttempted = true;

          const getTurnstileToken = getTurnstileTokenRef.current;
          const turnstileToken = getTurnstileToken
            ? await getTurnstileToken()
            : undefined;

          const newChatId = await mintFreshSessionSilent({ turnstileToken });
          dispatch({ type: "chatId/replaced", chatId: newChatId });
          await attempt(newChatId);
        }
      };

      await attempt(chatId);
    },
    [streamAssistantResponse, mintFreshSessionSilent]
  );

  const runStream = useCallback(
    async (chatId: string, message: string): Promise<void> => {
      const userMsg: PublicChatMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content: message,
        created_at: new Date().toISOString(),
      };
      dispatch({
        type: "submit/start",
        userMessage: userMsg,
        pending: message,
      });
      await streamWithRecovery(chatId, message);
    },
    [streamWithRecovery]
  );

  const bootstrap = useCallback(
    async (args?: BootstrapArgs) => {
      // StrictMode double-mount: dedupe concurrent calls so we don't create two sessions.
      if (bootstrapPromiseRef.current) return bootstrapPromiseRef.current;

      const run = async (): Promise<string | null> => {
        try {
          let token = getToken(variant);
          let chatId = getChatId(variant);

          if (!token || !chatId) {
            dispatch({ type: "session/start" });
            chatId = await mintFreshSessionSilent({
              turnstileToken: args?.turnstileToken,
            });
            token = getToken(variant);
          }

          chatIdRef.current = chatId;
          dispatch({ type: "session/started", chatId });
          const saved = getSavedMessages(variant);
          dispatch({ type: "history/loaded", messages: saved ?? [] });
          hasBootstrappedRef.current = true;
          return chatId;
        } catch (e) {
          const msg = e instanceof Error ? e.message : "bootstrap_failed";
          if (msg.includes("401")) clearToken(variant);
          reportPublicJanError("bootstrap", e);
          dispatch({ type: "error", message: msg });
          return null;
        } finally {
          bootstrapPromiseRef.current = null;
        }
      };

      bootstrapPromiseRef.current = run();
      return bootstrapPromiseRef.current;
    },
    [mintFreshSessionSilent, variant]
  );

  const submit = useCallback(
    async (message: string) => {
      // Prefer the ref so a freshly-minted chatId is usable before state
      // catches up (rapid submit after bootstrap).
      const chatId = chatIdRef.current ?? state.chatId;
      if (!chatId) {
        reportPublicJanError(
          "submit",
          new Error("submit called without chatId")
        );
        dispatch({ type: "error", message: "no_chat_id" });
        return;
      }
      try {
        await runStream(chatId, message);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "submit_failed";
        reportPublicJanError("submit", e);
        dispatch({ type: "error", message: msg });
      }
    },
    [state.chatId, runStream]
  );

  const submitEmail = useCallback(
    async (email: string, professionId: string) => {
      try {
        await apiSubmitEmail(email, professionId, variant);
        // Announce the lead to the host page's tag manager so GTM can fire the
        // Google Ads conversion. Only after the API succeeds, so we don't
        // count a conversion the backend never stored.
        pushJanEmailSubmit({ variant, professionId });
        dispatch({ type: "email/submitted" });
      } catch (e) {
        const msg = e instanceof Error ? e.message : "email_submit_failed";
        reportPublicJanError("email-submit", e);
        dispatch({ type: "error", message: msg });
        throw e;
      }
    },
    [variant]
  );

  const retryPending = useCallback(async () => {
    const chatId = chatIdRef.current ?? state.chatId;
    if (!chatId || !state.pendingMessage) return;
    // submit/resume — not submit/start — because the user bubble for this
    // pending message was already appended on the original submit. Going
    // through runStream here would duplicate it.
    dispatch({ type: "submit/resume" });
    try {
      await streamWithRecovery(chatId, state.pendingMessage);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "submit_failed";
      reportPublicJanError("retry-pending", e);
      dispatch({ type: "error", message: msg });
    }
  }, [state.chatId, state.pendingMessage, streamWithRecovery]);

  const reset = useCallback(() => {
    clearToken(variant);
    chatIdRef.current = null;
    hasBootstrappedRef.current = false;
    dispatch({ type: "reset" });
  }, [variant]);

  return { ...state, bootstrap, submit, submitEmail, retryPending, reset };
};
